//@ts-check

import React, { useContext } from "react";
import { Field, useForm, useField, FieldRenderProps } from "react-final-form";
import cc from "classcat";
import areDeepEqual from "fast-deep-equal";
import {
  AlertDialogLabel,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogContent,
} from "@reach/alert-dialog";
import DateTime from "@nateradebaugh/react-datetime";
import "./styles.css";
import axios from "axios";
import { QuotationProduct } from "./types";
import rawFormatDate from "date-fns/format";
import { useSecurable } from "./lib/security/components/SecurityProvider";

const { useState, useEffect, useLayoutEffect, useRef, useCallback } = React;

let uid = 0;
function useUid(preferredId: any) {
  const [id, setId] = useState(preferredId);

  useEffect(() => {
    if (!id) {
      setId(++uid);
    }
  }, [id]);

  return id;
}

export function usePrevious<T>(value: T): T | undefined {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T | undefined>();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export function useFieldValue<T>(name: string, defaultValue: T): T {
  const {
    input: { value = defaultValue },
  } = useField(name);

  return value ?? defaultValue;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fullDataUrl = reader.result as string;
      // Data URI looks like data:[<mime type>][;charset=<charset>][;base64],<encoded data>
      // but we only want the base64 part, so we split and get the parts after the comma
      // https://css-tricks.com/data-uris/
      const asBase64 = fullDataUrl.split("base64,")[1];

      resolve(asBase64);
    };
    reader.onerror = (error) => reject(error);
  });
}

export async function getRemoteList<T>(
  url: string,
  noCache: boolean = false
): Promise<T[]> {
  try {
    // Add a timestamp to prevent caching of lists in IE
    if (noCache && isInternetExplorer()) {
      const timestamp = "_timestamp=" + new Date().getTime();
      if (url.indexOf("?") !== -1) {
        url = url + "&" + timestamp;
      } else {
        url = url + "?" + timestamp;
      }
    }

    const response = await axios.get(url);

    if (response && response.data) {
      if (Array.isArray(response.data)) {
        return response.data;
      }

      const items = response.data.Items;
      if (Array.isArray(items)) {
        return items;
      }
    }

    throw response;
  } catch (error) {
    console.error(error);
  }

  return [];
}

export function useIsSecurableVisible(securableName) {
  const details = useSecurable(securableName, "Visible");
  const isVisible = details?.actionPermissions?.Visible || false;

  return isVisible;
}

export function safeNumber(val) {
  const asNumber = Number.parseFloat(val);
  if (Number.isNaN(asNumber)) {
    return 0;
  }

  return asNumber;
}

function isNumericField(props) {
  return (
    props.type === "number" ||
    typeof props.decimals === "number" ||
    typeof props.min === "number" ||
    typeof props.max === "number"
  );
}

function isDateTimeField(props) {
  return (
    props.type === "date" || props.type === "time" || props.type === "datetime"
  );
}

export function formatDate(value, props: any = {}) {
  if (!value) {
    if (typeof props.placeholder === "string") {
      return props.placeholder;
    }

    return "";
  }

  let dateTimeFormat = "";
  if (typeof props.format === "string") {
    dateTimeFormat = props.format;
  } else {
    switch (props.type) {
      case "time":
        dateTimeFormat = "hh:mm A";
        break;

      case "datetime":
        dateTimeFormat = "MM/dd/yyyy hh:mm A";
        break;

      case "date":
      default:
        dateTimeFormat = "MM/dd/yyyy";
        break;
    }
  }

  return rawFormatDate(value, dateTimeFormat);
}

function getDisplayText(value, props) {
  return (
    <>
      {!!props.prepend && props.prepend}
      {isNumericField(props)
        ? formatNumber(value, props)
        : isDateTimeField(props)
        ? formatDate(value, props)
        : value === true
        ? "Yes"
        : value === false
        ? "No"
        : value === undefined || value === ""
        ? props.defaultText
        : value}
      {!!props.append && props.append}
    </>
  );
}

export function Icon({ className, ...props }) {
  return <i className={className} {...props} />;
}

export function Tooltip({ children, title = undefined, ...rest }) {
  useEffect(() => {
    $('[data-toggle="tooltip"]').tooltip({
      delay: { show: 1000, hide: 1000 },
      content: function () {
        return $(this).prop("title");
      },
    });
  }, []);

  if (!title) {
    return children;
  }

  return (
    <span data-toggle="tooltip" title={title} {...rest}>
      {children}
    </span>
  );
}

export function DisplayField({ name, className = undefined, ...props }) {
  return (
    <Field name={name}>
      {({ input }) => (
        <div className={cc(["form-text", className])}>
          {getDisplayText(input.value, props)}
        </div>
      )}
    </Field>
  );
}

export function ProductLinkField({ name, className = undefined, ...props }) {
  return (
    <Field name={name}>
      {({ input }) => {
        const product: QuotationProduct = input.value;

        const productNumber = product.AddCalibration
          ? product.CalibrationProductNumber
          : product.ProductNumber;
        const displayText = getDisplayText(productNumber, props);

        return (
          <>
            {product.HasDetails ? (
              <a
                href={`/Product/Product/View?id=${productNumber}`}
                className={cc(["form-text text-nowrap", className])}
              >
                {displayText}
              </a>
            ) : (
              <div className={cc(["form-text text-nowrap", className])}>
                {displayText}
              </div>
            )}
          </>
        );
      }}
    </Field>
  );
}

export function TextField({
  label = undefined,
  id = undefined,
  component = "input" as "input" | "textarea",
  prepend = undefined,
  append = undefined,
  style = undefined,
  className = undefined,
  loading = false,
  loadingMessage = undefined,
  showRequiredTextWhenRequired = false,
  maxLength = undefined as number | undefined,
  ...props
}) {
  const fieldId = useUid(id);
  const isPrependOrAppend = prepend || append;

  const inputProps = { id: fieldId, ...props };
  inputProps.className = cc(["form-control", className]);
  if (!isPrependOrAppend) {
    inputProps.style = style;
  }

  if (maxLength) {
    inputProps.validate = (value) => {
      let passedValidation = true;
      if (value && typeof value === "string") {
        passedValidation = value.length <= maxLength;
      }

      return passedValidation
        ? undefined
        : `Field length cannot exceed ${maxLength}`;
    };
  }

  const inputEl = loading ? (
    <input
      id={fieldId}
      disabled
      className="form-control"
      value={loadingMessage}
    />
  ) : isNumericField(props) ? (
    <NumberField {...inputProps} />
  ) : (
    <Field
      name={inputProps.name}
      component={component}
      type="text"
      {...inputProps}
    />
  );

  let editField = undefined;
  if (isPrependOrAppend) {
    editField = (
      <>
        <div className={cc(["input-group"])} style={style}>
          {!!prepend && (
            <div className="input-group-prepend">
              <div className="input-group-text text-dark p-1">{prepend}</div>
            </div>
          )}
          {inputEl}
          {!!append && (
            <div className="input-group-append">
              <div className="input-group-text text-dark p-1">{append}</div>
            </div>
          )}
        </div>
        {props.required && showRequiredTextWhenRequired && (
          <Field name={inputProps.name}>
            {({ input }) => {
              if (input.value) {
                return null;
              }

              return (
                <div>
                  <em className="text-danger">Required</em>
                </div>
              );
            }}
          </Field>
        )}
      </>
    );
  } else {
    editField = (
      <>
        {inputEl}
        {props.required && showRequiredTextWhenRequired && (
          <Field name={inputProps.name}>
            {({ input }) => {
              if (input.value) {
                return null;
              }

              return (
                <div>
                  <em className="text-danger">Required</em>
                </div>
              );
            }}
          </Field>
        )}
        {inputProps.validate && (
          <Field name={inputProps.name}>
            {({ input, meta }) => {
              if (!meta.error || !meta.touched) {
                return null;
              }

              return (
                <div>
                  <em className="text-danger">{meta.error}</em>
                </div>
              );
            }}
          </Field>
        )}
      </>
    );
  }

  return (
    <>
      {label ? (
        <label htmlFor={fieldId} className={cc([props.required && "required"])}>
          {label}
        </label>
      ) : null}
      {editField}
    </>
  );
}

export function DateTimeField({
  label = undefined,
  id = undefined,
  prepend = undefined,
  append = undefined,
  style = undefined,
  className = undefined,
  name,
  validate = undefined,
  ...props
}) {
  const fieldId = useUid(id);
  const isPrependOrAppend = prepend || append;

  const inputProps = { autoComplete: "off", ...props };
  if (!isPrependOrAppend) {
    inputProps.style = style;
    inputProps.className = cc(["form-control", className]);
  }

  const inputEl = (
    <Field type="text" name={name} validate={validate}>
      {({ input, meta }) => {
        return <DateTime {...input} {...inputProps} />;
      }}
    </Field>
  );

  let editField = undefined;
  if (isPrependOrAppend) {
    editField = (
      <div className={cc(["input-group", className])} style={style}>
        {!!prepend && (
          <div className="input-group-prepend">
            <div className="input-group-text text-dark p-1">{prepend}</div>
          </div>
        )}
        {inputEl}
        {!!append && (
          <div className="input-group-append">
            <div className="input-group-text text-dark p-1">{append}</div>
          </div>
        )}
      </div>
    );
  } else {
    editField = inputEl;
  }

  return (
    <>
      {label ? (
        <label htmlFor={fieldId} className={cc([props.required && "required"])}>
          {label}
        </label>
      ) : null}
      {editField}
    </>
  );
}

export function DateField(props) {
  return <DateTimeField {...props} timeFormat={false} />;
}

export function TimeField(props) {
  return <DateTimeField {...props} dateFormat={false} />;
}

export function CheckboxField({
  label = undefined,
  className = undefined,
  onChange = undefined,
  labelAfter = undefined,
  ...props
}) {
  return (
    <Field name={props.name} {...props} type="checkbox">
      {({ input }) => {
        function handleChange(e) {
          if (typeof input.onChange === "function") {
            input.onChange(e);
          }

          if (typeof onChange === "function") {
            onChange(e);
          }
        }

        if (labelAfter) {
          return (
            <div className="form-check form-check-inline">
              <label className="d-flex align-items-center">
                <input
                  type="checkbox"
                  {...props}
                  {...input}
                  id={props.name}
                  onChange={handleChange}
                />{" "}
                {labelAfter}
              </label>
            </div>
          );
        }

        return (
          <div className={cc(["form-check p-0", className])}>
            <label className="form-check-label">
              {label}
              <input
                {...props}
                {...input}
                id={props.name}
                type="checkbox"
                className="form-check-input ml-2"
                onChange={handleChange}
              />
            </label>
          </div>
        );
      }}
    </Field>
  );
}

export type Validator = (value: any) => undefined | string;

export function RequiredIndicator() {
  return <span style={{ color: "#cc0000" }}> *</span>;
}

export function FieldError({
  touched = false,
  error = undefined,
  submitError = undefined,
  dirtySinceLastSubmit = false,
}) {
  const clientError = touched && error;
  const serverError = !dirtySinceLastSubmit && submitError;
  if (serverError || clientError) {
    return (
      <div style={{ fontSize: "0.8rem", color: "#cc0000" }}>
        {serverError || clientError}
      </div>
    );
  }

  return null;
}

export const RadioGroupContext = React.createContext(false);

export interface RadioGroupProps {
  className?: string;
  style?: any;
  label?: string;
  name: string;
  children: any;
  required?: boolean;
  inline?: boolean;
}

export function RadioGroup(props: RadioGroupProps) {
  const {
    className,
    style,
    name,
    label,
    required,
    children,
    inline = false,
  } = props;

  return (
    <React.Fragment>
      {label ? (
        <span>
          {label}
          {required ? <RequiredIndicator /> : null}
        </span>
      ) : null}
      <div
        className={cc([className, { "d-inline w-auto": inline }])}
        style={style}
      >
        <Field name={name}>
          {({ meta }) => {
            return (
              <RadioGroupContext.Provider value={true}>
                {children}
                <FieldError {...meta} />
              </RadioGroupContext.Provider>
            );
          }}
        </Field>
      </div>
    </React.Fragment>
  );
}

export interface RadioFieldProps {
  name: string;
  label?: string;
  value?: any;
  onChange?: (isInputChecked: boolean) => void;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  style?: any;
  validate?: Validator;
}

export type RadioFieldAdapterProps = RadioFieldProps &
  FieldRenderProps<HTMLInputElement>;

function RadioFieldAdapter(props: RadioFieldAdapterProps): JSX.Element {
  const {
    className,
    style,
    input,
    meta,
    label,
    required,
    onChange,
    disabled,
    readOnly,
    value: thisValue,
    ...rest
  } = props;

  const isInRadioGroup = useContext(RadioGroupContext);

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    input.onChange(e);
    input.onBlur(e as any);

    if (onChange) {
      const isChecked = e && e.target ? e.target.checked : false;
      onChange(isChecked);
    }
  }

  return (
    <React.Fragment>
      <label className={cc(["mb-0 pb-0", className])} style={style}>
        <input
          {...input}
          value={input.value as any}
          {...rest}
          disabled={disabled || readOnly}
          onChange={onInputChange}
        />
        <span>
          <span>
            {label ? (
              <React.Fragment>
                {" "}
                {label}
                {!isInRadioGroup && required ? <RequiredIndicator /> : null}
              </React.Fragment>
            ) : null}
          </span>
        </span>
      </label>
      {!isInRadioGroup && <FieldError {...meta} />}
    </React.Fragment>
  );
}

export function RadioField(props: RadioFieldProps): JSX.Element {
  return <Field {...props} type="radio" component={RadioFieldAdapter} />;
}

interface FormatNumberOptions {
  decimals?: number | undefined;
  min?: number | undefined;
  max?: number | undefined;
  nullable?: boolean | undefined;
}
export function formatNumber(
  value: number | string,
  {
    decimals = undefined,
    min = undefined,
    max = undefined,
    nullable = false,
  }: FormatNumberOptions
): string {
  function intFormat(num: number | string, decimals: number): string {
    if (decimals >= 0) {
      return num.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    return num === undefined || num === null ? "" : `${num}`;
  }

  const asNumber =
    typeof value === "number"
      ? value
      : Number.parseFloat(value?.replace(",", ""));
  if (Number.isNaN(asNumber)) {
    if (!nullable && min !== undefined) {
      return intFormat(min, decimals);
    }

    return intFormat(value, decimals);
  }

  let inRange = asNumber;
  if (typeof min === "number") {
    inRange = Math.max(min, inRange);
  }

  if (typeof max === "number") {
    inRange = Math.min(max, inRange);
  }

  const asRounded = intFormat(inRange, decimals);
  return asRounded;
}

function normalizeNumber(value, options) {
  if (typeof value === "string") {
    // Make sure there's just numbers and decimals
    if (options.decimals > 0) {
      value = value.replace(/[^.\d]/g, "");

      // If there's more than 1 decimal, remove the last one
      const numberParts: string[] = value.split(".");
      if (numberParts.length > 2) {
        value = numberParts.slice(0, 2).join(".");
      }

      // If there's a leading decimal, prepend a 0
      if (value.startsWith(".")) {
        value = `0${value}`;
      }
    } else {
      // If there's no decimals, just show numbers
      value = value.replace(/[^\d]/g, "");
    }
  }

  if (typeof options.max === "number" && value > options.max) {
    value = options.max;
  }

  if (typeof options.min === "number" && value < options.min) {
    value = options.min;
  }

  return value;
}

function NumberField(props) {
  const { nullable, ...rest } = props;

  return (
    <Field
      component="input"
      type="text"
      className="form-control"
      parse={(value) => normalizeNumber(value, props)}
      {...rest}
    />
  );
}

export function SelectField({
  label = undefined,
  id = undefined,
  name,
  options,
  onChange = undefined,
  loading = false,
  loadingMessage = undefined,
  select2 = false as boolean | Record<string, any>,
  select2Init = undefined as undefined | (() => void),
  ...props
}) {
  const form = useForm();
  const fieldId = useUid(id);

  useEffect(() => {
    if (select2) {
      const item = $("#" + fieldId);
      item.select2(
        select2 === true
          ? {
              theme: "bootstrap",
              minimumResultsForSearch: 8,
            }
          : select2
      );

      item.on("change", () => {
        const newVal = $("#" + fieldId).val();
        form.change(name, newVal);
      });

      if (typeof select2Init === "function") {
        select2Init();
      }
    }
  }, []);

  let editField = undefined;
  if (loading) {
    editField = (
      <select disabled className="form-control" id={fieldId}>
        {loadingMessage ? <option>{loadingMessage}</option> : null}
      </select>
    );
  } else {
    editField = (
      <Field name={name}>
        {({ input }) => {
          function handleChange(e) {
            input.onChange(e);

            if (typeof onChange === "function") {
              onChange(e);
            }
          }

          return (
            <select
              id={fieldId}
              {...props}
              {...input}
              onChange={handleChange}
              className="form-control"
            >
              {options}
            </select>
          );
        }}
      </Field>
    );
  }

  return (
    <>
      {label ? (
        <label htmlFor={fieldId} className={cc([props.required && "required"])}>
          {label}
        </label>
      ) : null}
      {editField}
    </>
  );
}

export function CalculateFields({
  rowName = undefined,
  name = rowName,
  calculate,
}) {
  const form = useForm();
  const [calculatedFields, setCalculatedFields] = useState(undefined);
  const calculatedEntries = calculatedFields
    ? Object.entries(calculatedFields)
    : [];

  useEffect(() => {
    form.batch(() => {
      for (const [key, value] of calculatedEntries) {
        form.change(`${name}.${key}`, value);
      }
    });
  }, [form, calculatedFields, name, calculatedEntries]);

  return (
    <Field name={name}>
      {({ input }) => {
        const row = input.value;

        // Async-ify
        setTimeout(() => {
          const calculated = calculate(row);
          if (!areDeepEqual(calculated, calculatedFields)) {
            setCalculatedFields(calculated);
          }
        }, 1);

        return null;
      }}
    </Field>
  );
}

export function DialogButton({
  renderButton,
  title,
  renderContent,
  className = undefined,
  style = undefined,
}) {
  const cancelRef = useRef();
  const [isOpen, setIsOpen] = useState(false);

  function show() {
    setIsOpen(true);
  }

  function hide() {
    setIsOpen(false);
  }

  const contentProps = {
    show: show,
    hide: hide,
    cancelButtonProps: {
      type: "button",
      onClick: hide,
      ref: cancelRef,
    },
    showButtonProps: {
      type: "button",
      onClick: show,
    },
  };

  return (
    <>
      {renderButton({ ...contentProps.showButtonProps })}

      {isOpen && (
        <AlertDialogOverlay
          className="modal-backdrop"
          style={{ backgroundColor: "hsla(0, 0%, 0%, 0.3)" }}
          leastDestructiveRef={cancelRef}
        >
          <AlertDialogContent className="modal-dialog">
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: "transparent" }}
            >
              <div
                className={cc([
                  "modal-dialog modal-dialog-scrollable",
                  className,
                ])}
                style={style}
                role="document"
              >
                <div className="modal-content">
                  <AlertDialogLabel className="modal-header">
                    <h2 className="modal-title">{title}</h2>
                  </AlertDialogLabel>
                  <AlertDialogDescription>
                    {renderContent(contentProps)}
                  </AlertDialogDescription>
                </div>
              </div>
            </div>
          </AlertDialogContent>
        </AlertDialogOverlay>
      )}
    </>
  );
}

export function ConfirmDialogButton(props) {
  const {
    title = "Confirm",
    onConfirm,
    confirmText = "Confirm",
    cancelText = "Cancel",
    renderContent: renderConfirmDialogContent,
    ...rest
  } = props;

  return (
    <DialogButton
      {...rest}
      renderButton={props.renderButton}
      title={title}
      renderContent={(dialogContentProps) => {
        const onClick = () => onConfirm(dialogContentProps);

        return (
          <>
            <div className="modal-body">
              {typeof renderConfirmDialogContent === "function"
                ? renderConfirmDialogContent()
                : renderConfirmDialogContent}
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onClick}>
                {confirmText}
              </button>{" "}
              <button
                {...dialogContentProps.cancelButtonProps}
                className="btn btn-outline-secondary"
              >
                {cancelText}
              </button>
            </div>
          </>
        );
      }}
    />
  );
}

function getSize(el) {
  if (!el) {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
  };
}

export function useComponentSize(ref) {
  const [componentSize, setComponentSize] = useState(
    getSize(ref ? ref.current : {})
  );

  const handleResize = useCallback(
    function handleResize() {
      if (ref.current) {
        setComponentSize(getSize(ref.current));
      }
    },
    [ref]
  );

  useLayoutEffect(
    function () {
      if (!ref.current) {
        return;
      }

      handleResize();

      if (typeof ResizeObserver === "function") {
        let resizeObserver = new ResizeObserver(function () {
          handleResize();
        });
        resizeObserver.observe(ref.current);

        return function () {
          resizeObserver.disconnect(ref.current);
          resizeObserver = null;
        };
      } else {
        window.addEventListener("resize", handleResize);

        return function () {
          window.removeEventListener("resize", handleResize);
        };
      }
    },
    [ref.current]
  );

  return componentSize;
}

export function useAsyncList<T>(
  func: () => Promise<T[]>
): readonly [T[], () => Promise<T[]>] {
  const [list, setList] = useState([]);

  async function rawGetList() {
    const responseList: T[] = await func();
    setList(responseList);

    return responseList;
  }

  return [list, rawGetList] as const;
}

function isInternetExplorer() {
  const agent = window.navigator.userAgent;

  return agent.indexOf("MSIE ") !== -1 || agent.indexOf("Trident/") !== -1;
}
